import React, { useEffect } from 'react';
import { statusEffectManager } from '../combat/StatusEffectManager';

/**
 * StatusEffectBridge — listens for the L3.4 server broadcasts
 * (`statusApplied`, `statusTick`, `statusRemoved`) and feeds them into
 * the local `statusEffectManager` so VFX renders for EVERY viewer, not
 * just the caster.
 *
 * Pre-L3.4, status effects were applied client-side only — the caster
 * saw poison clouds, but spectators / target's local view saw nothing.
 * The new server-authoritative path (StatusEffectService) emits these
 * three messages on every viewer's client; this bridge converts them
 * into the same `statusEffectManager.apply()` / `.remove()` calls the
 * caster's HitEffects already make, so the existing VFXSystem
 * subscribers (`vfx:status_effect`, `vfx:status_stack_update`) light
 * up automatically.
 *
 * Anti-double-VFX: the caster's HitEffects path also calls
 * `statusEffectManager.apply()` locally for prediction. That apply()
 * happens FIRST; when the server's `statusApplied` round-trips back
 * in ~50–200ms, this bridge calls apply() again — `apply()` treats
 * the second call as a stack-refresh (idempotent), so no duplicate
 * particle spawn.
 *
 * This bridge subscribes through `NetworkManager.onMessage()` rather than
 * calling `room.onMessage()` directly. Colyseus only keeps one handler per
 * message type, and NetworkManager already owns the authoritative
 * `statusApplied`/`statusRemoved`/`statusTick` listeners. Subscribing via
 * NetworkManager lets both the EventBus wire consumers and this bridge
 * receive the same messages without clobbering each other.
 *
 * Plan: docs/plans (L3.4)
 */

const POLL_INTERVAL_MS = 500;

const StatusEffectBridgeInner: React.FC = () => {
  useEffect(() => {
    let boundNm: any = null;
    let unsubscribe: (() => void) | null = null;

    const handleMessage = (type: string, msg: any) => {
      if (type === 'statusApplied') {
        if (!msg?.targetId || !msg?.effectId) return;
        statusEffectManager.apply({
          targetId: String(msg.targetId),
          effectId: String(msg.effectId),
          sourceId: String(msg.sourceSid ?? ''),
          duration: Number(msg.durationMs) || 3000,
          tickDamage: typeof msg.tickDamage === 'number' ? msg.tickDamage : 0,
          tickRateMs: typeof msg.tickRateMs === 'number' ? msg.tickRateMs : 1000,
          data: msg.data && typeof msg.data === 'object' ? msg.data : null,
          stack: false,
          // No maxStacks on the server payload (server doesn't track
          // stacks at MVP — it refreshes); apply() defaults to one stack.
        });
      } else if (type === 'statusRemoved') {
        if (!msg?.targetId || !msg?.effectId) return;
        statusEffectManager.remove(String(msg.targetId), String(msg.effectId));
      } else if (type === 'statusTick') {
        // statusTick is purely a "draw a tiny tick puff" signal — the
        // damage number itself comes via the server's `playerDamaged`
        // broadcast which DamagePresentationService already handles. Bridge
        // consumes the message so the wildcard logger doesn't flag it as
        // orphan. Future: emit 'vfx:status_tick_puff' for a per-tick particle.
      }
    };

    const tryBind = () => {
      const nm = (globalThis as any).nm;
      if (!nm || nm === boundNm || typeof nm.onMessage !== 'function') return;

      boundNm = nm;
      unsubscribe = nm.onMessage(handleMessage) as (() => void);
    };

    tryBind();
    const interval = setInterval(tryBind, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      unsubscribe?.();
    };
  }, []);

  return null;
};

export const StatusEffectBridge: React.FC = () => <StatusEffectBridgeInner />;
